import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../../utils/axios";

const steps = [
    "tickets",
    "attendee",
    "payment"
]

const initialState = {
    selectedTickets: [],
    activeStep: steps[0],
    totalTickets: 0,
    attendeesData: [],
    ticketTypesWithIds: [],
    bookingId: null,
    orderSummary: {
        bookingId: null,
        eventId: null,
        bookingDetails: {},
        ticketSummary: [],
        cartSummary: {
            totalTicketsQty: null,
            subTotalWithoutTax: null,
            taxAmount: null,
            subTotalWithTax: null,
            subTotalWithTaxBreakup: {},
            taxAmountBreakup: {},
            fees: null,
            totalPayableAmount: null,
            bookingFees: {},
            platformFees: {}
        }
    }
}

// fees -> booking fees + platform fees


const calculateTotalTickets = (tickets) => {
    return tickets.reduce((sum, ticket) => sum + ticket.quantity, 0);
};

const ticketBuyingSlice = createSlice({
    name: "ticketBuying",
    initialState,
    reducers: {
        incrementTicket: (state, action) => {
            // extract payload
            const {
                ticketId,
                name,
                price,
                description,
                isGroupTicket,
                groupTicketAttendees,
                limit
            } = action.payload;


            // check if the ticket already exists
            const existingTicket = state.selectedTickets.find(
                (ticket) => ticket.ticketId === ticketId
            );


            // if ticket is already exists and within limit then increment the quantity instead of creating new object
            // Else create new object
            if (existingTicket) {
                if (existingTicket.quantity < limit) {
                    existingTicket.quantity += 1;
                }
            } else {
                state.selectedTickets.push({
                    ticketId,
                    quantity: 1,
                    name,
                    price,
                    description,
                    isGroupTicket,
                    groupTicketAttendees,
                });
            }

            // update the totalTickets state
            state.totalTickets = calculateTotalTickets(state.selectedTickets);
        },
        decrementTicket: (state, action) => {
            // extract the payload
            const { ticketId } = action.payload;


            // check if the ticket already exists
            const existingTicket = state.selectedTickets.find(
                (ticket) => ticket.ticketId === ticketId
            );


            // if ticket is already exists then decrement the quantity
            if (existingTicket) {
                existingTicket.quantity -= 1;

                // If quantity is 0 then remove the ticket from the selectedTickets array
                if (existingTicket.quantity <= 0) {
                    state.selectedTickets = state.selectedTickets.filter(
                        (ticket) => ticket.ticketId !== ticketId
                    );
                }
            }

            // update the totalTickets state
            state.totalTickets = calculateTotalTickets(state.selectedTickets);
        },
        setActiveStep: (state, action) => {
            state.activeStep = action.payload;
        },
        setAttendeesData: (state, action) => {
            state.attendeesData = action.payload;
        },

        // Reset the ticket buying state before add new tickets
        resetTicketBuyingState: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(createTicketBooking.fulfilled, (state, action) => {
                const ticketTypeInfo = action.payload.data.ticket_type_info;
                state.bookingId = action.payload.data.id;

                const normalizedTickets = {};

                ticketTypeInfo.forEach(ticketType => {
                    const typeId = ticketType.ticket_type_id;
                    const tickets = ticketType.tickets || [];

                    normalizedTickets[typeId] = {
                        count: tickets.length,
                        ids: tickets.map(ticket => ticket.id),
                        title: ticketType.title,
                        description: ticketType.description
                    };
                });

                state.ticketTypesWithIds = normalizedTickets;

                // console.log("🎯 Normalized Tickets in Redux:", normalizedTickets);
            })

        builder
            .addCase(getTicketBookingById.fulfilled, (state, action) => {
                const { data } = action.payload;

                state.orderSummary.bookingId = data.id;
                state.orderSummary.eventId = data.event_id;
                state.orderSummary.bookingDetails = {
                    name: data.invoice_name,
                    email: data.invoice_email,
                    phone: data.invoice_phone,
                    stateId: data.state_id,
                    igstRate: data.igst_rate,
                    cgstRate: data.cgst_rate,
                    sgstRate: data.sgst_rate,
                    utgstRate: data.utgst_rate
                }

                state.orderSummary.ticketSummary = data.ticket_type_info.map(ticket => ({
                    ticketTypeId: ticket.ticket_type_id,
                    ticketName: ticket.title,
                    description: ticket.description,
                    tickets: ticket.tickets.map(subTicket => ({
                        purchaseTicketId: subTicket.id,
                        attendeeName: subTicket.attendee_name,
                        attendeeEmail: subTicket.attendee_email,
                        attendeePhone: subTicket.attendee_phone,
                        priceExclusive: subTicket.price_exclusive,
                        priceInclusive: subTicket.price_inclusive,
                        parentId: subTicket.parent_id,
                        taxAmount: subTicket.tax_amount,
                        cgstAmount: subTicket.cgst_amount,
                        sgstAmount: subTicket.sgst_amount,
                        utgstAmount: subTicket.utgst_amount,
                        igstAmount: subTicket.igst_amount,
                    }))
                }));


                /**
                 * Flatten all tickets once and filter whose parent id is null
                 */
                const allTickets = data.ticket_type_info
                    .flatMap(ticket => ticket.tickets)
                    .filter(subTicket => subTicket.parent_id === null);


                /**
                 * Calculate subtotal without tax
                 * This is the sum of all ticket prices excluding tax
                 */
                const subTotalWithoutTax = allTickets.reduce((sum, subTicket) => sum + parseFloat(subTicket.price_exclusive), 0);


                /**
                 * Calculate total tax amount
                 * This is the sum of all ticket taxes
                 */
                const taxAmount = allTickets.reduce((sum, subTicket) => sum + parseFloat(subTicket.tax_amount), 0);


                /**
                 * Calculate tax amount breakup
                 * This is the breakup of all ticket taxes
                 * For each ticket, add its tax amounts to the respective keys in the breakup object
                 */
                const taxAmountBreakup = allTickets.reduce((breakup, subTicket) => {
                    return {
                        cgstAmount: (parseFloat(breakup.cgstAmount || 0) + parseFloat(subTicket.cgst_amount || 0)).toFixed(2),
                        sgstAmount: (parseFloat(breakup.sgstAmount || 0) + parseFloat(subTicket.sgst_amount || 0)).toFixed(2),
                        igstAmount: (parseFloat(breakup.igstAmount || 0) + parseFloat(subTicket.igst_amount || 0)).toFixed(2),
                        utgstAmount: (parseFloat(breakup.utgstAmount || 0) + parseFloat(subTicket.utgst_amount || 0)).toFixed(2)
                    };
                }, {});


                /**
                 * Calculate subtotal with tax
                 * This is the sum of all ticket prices including tax
                 */
                const subTotalWithTax = allTickets.reduce((sum, subTicket) => sum + parseFloat(subTicket.price_inclusive), 0);


                /**
                 * Calculate the breakup of all ticket prices
                 * This will give us the detailed breakdown of each ticket's pricing
                 */
                const ticketsBreakup = data.ticket_type_info.map(ticket => {

                    const totalQty = ticket.tickets.length;
                    const ticketPrice = ticket.tickets[ticket.tickets.length - 1].price_exclusive;

                    /**
                     * Ticket breakup table related calculation
                     */
                    const gstValueOnTicketPriceIntoQty = ((ticketPrice * totalQty) * (parseFloat(ticket.gst_rate) / 100))
                    const ticketPriceWithTax = ticket.tickets[ticket.tickets.length - 1].price_inclusive;

                    /**
                     * Booking breakup table related calculation
                     */
                    const bookingTotal = ticket.booking_fee * totalQty;

                    /**
                     * Platform breakup table related calculation
                    */
                    const commissionRate = ticket.commission_rate === null ? 0 : ticket.commission_rate;
                    const commissionAmount = ticket.commission_amount === null ? 0 : ticket.commission_amount;
                    const fixedRupeeCharges = commissionAmount * totalQty;
                    const percentageCharges = ((ticketPrice * totalQty) * (commissionRate / 100));
                    const totalCharges = fixedRupeeCharges + percentageCharges;

                    return {
                        ticketName: ticket.title,
                        quantity: totalQty,
                        price: ticketPrice,
                        totalAmount: (ticketPrice * totalQty).toFixed(2),
                        gstRate: ticket.gst_rate,
                        gstValueOnTicketPrice: gstValueOnTicketPriceIntoQty,
                        totalAmountWithGST: parseFloat(ticketPriceWithTax * totalQty).toFixed(2),
                        bookingFee: ticket.booking_fee,
                        commissionRate: commissionRate,
                        commissionAmount: commissionAmount,
                        totalCharges: totalCharges,
                        fixedRupeeCharges: fixedRupeeCharges,
                        percentageCharges: percentageCharges,
                        bookingTotal: bookingTotal
                    };
                });


                /**
                 * Fees related calculations
                 */
                const totalBookingFeesInclusive = data.booking_fee_info?.booking_fee_inclusive === null ? 0 : parseFloat(data.booking_fee_info?.booking_fee_inclusive)
                const totalPlatformFeesInclusive = data.platform_fee_info?.platform_fee_inclusive === null ? 0 : parseFloat(data.platform_fee_info?.platform_fee_inclusive)
                const totalFees = totalBookingFeesInclusive + totalPlatformFeesInclusive

                /**
                 * Update the cart summary
                 */
                state.orderSummary.cartSummary = {
                    totalTicketsQty: allTickets.length,
                    subTotalWithoutTax: subTotalWithoutTax.toFixed(2),
                    taxAmount: taxAmount.toFixed(2),
                    taxAmountBreakup: taxAmountBreakup,
                    subTotalWithTax: subTotalWithTax.toFixed(2),
                    totalPayableAmount: parseFloat(data.total_amount_payable).toFixed(2),
                    subTotalWithTaxBreakup: {
                        ticketBreakup: ticketsBreakup,
                        grossTotal: subTotalWithTax.toFixed(2)
                    },
                    bookingFees: {
                        bookingFeesExclusiveTax: parseFloat(data.booking_fee_info?.booking_fee_exclusive),
                        bookingFeesInclusiveTax: parseFloat(data.booking_fee_info?.booking_fee_inclusive),
                        totalBookingFees: parseFloat(data.booking_fee_info?.booking_fee_inclusive) + parseFloat(subTotalWithTax.toFixed(2)),
                        bookingFeesGstRate: data.booking_fee_info?.gst_rate,
                        taxBreakup: {
                            cgstRate: data.booking_fee_info?.cgst_rate,
                            sgstRate: data.booking_fee_info?.sgst_rate,
                            igstRate: data.booking_fee_info?.igst_rate,
                            utgstRate: data.booking_fee_info?.utgst_rate,
                            cgstAmount: data.booking_fee_info?.cgst_amount,
                            sgstAmount: data.booking_fee_info?.sgst_amount,
                            utgstAmount: data.booking_fee_info?.utgst_amount,
                            igstAmount: data.booking_fee_info?.igst_amount,
                        }
                    },
                    platformFees: {
                        platformFeesExclusiveTax: data.platform_fee_info?.platform_fee_exclusive === null ? 0 : parseFloat(data.platform_fee_info?.platform_fee_exclusive),
                        platformFeesInclusiveTax: data.platform_fee_info?.platform_fee_inclusive === null ? 0 : parseFloat(data.platform_fee_info?.platform_fee_inclusive),
                        platformFeesGstRate: data.platform_fee_info?.gst_rate === null ? 0 : data.platform_fee_info?.gst_rate,
                        taxBreakup: {
                            cgstRate: data.platform_fee_info?.cgst_rate === null ? 0 : data.platform_fee_info?.cgst_rate,
                            sgstRate: data.platform_fee_info?.sgst_rate === null ? 0 : data.platform_fee_info?.sgst_rate,
                            igstRate: data.platform_fee_info?.igst_rate === null ? 0 : data.platform_fee_info?.igst_rate,
                            utgstRate: data.platform_fee_info?.utgst_rate === null ? 0 : data.platform_fee_info?.utgst_rate,
                            cgstAmount: data.platform_fee_info?.cgst_amount === null ? 0 : data.platform_fee_info?.cgst_amount,
                            sgstAmount: data.platform_fee_info?.sgst_amount === null ? 0 : data.platform_fee_info?.sgst_amount,
                            utgstAmount: data.platform_fee_info?.utgst_amount === null ? 0 : data.platform_fee_info?.utgst_amount,
                            igstAmount: data.platform_fee_info?.igst_amount === null ? 0 : data.platform_fee_info?.igst_amount,
                        }
                    },
                    fees: totalFees
                };
            })
    }
});

export const {
    incrementTicket,
    decrementTicket,
    setActiveStep,
    setAttendeesData,
    resetTicketBuyingState
} = ticketBuyingSlice.actions;

export default ticketBuyingSlice.reducer;


/**
 * @example of payload:
 * {
 *   "event_id": 20,
 *   "user_id": 13,
 *   "tickets": [
 *     {
 *       "ticket_type_id": 250,
 *       "quantity": 1
 *     },
 *     {
 *       "ticket_type_id": 255,
 *       "quantity": 2
 *     }
 *   ]
 * }
 */
/**
 * @async
 * @function createTicketBooking
 * @description Creates a ticket booking by sending a POST request to the backend API.
 * @param {Object} bookingData - The data containing booking information.
 * @param {number} eventId - The ID of the event for which the booking is being created.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const createTicketBooking = createAsyncThunk(
    "ticketBuying/createTicketBooking",
    async ({ bookingData, eventId }, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/bookings`, bookingData, {
                headers: {
                    event_id: eventId
                }
            });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            // console.log("Response -> ", response)
            return response;
        } catch (error) {
            // console.error("🔥 Error creating ticket booking:", error);
            return rejectWithValue({
                ...error?.data,
                code: error?.status,
                statusText: error?.statusText
            });
        }
    }
);


/**
 * @async
 * @function mappingAttendeesToTicket
 * @description Maps attendees to their respective tickets by sending a POST request to the backend API.
 * @param {Object} attendeesData - The data containing attendees information to be mapped to tickets.
 * @param {boolean} passXheader - Flag to determine if the "Skip-Event-Active" header should be included.
 * @param {number} eventId - The ID of the event for which attendees are being mapped.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const mappingAttendeesToTicket = createAsyncThunk(
    "ticketBuying/mappingAttendeesToTicket",
    async ({ attendeesData, passXheader, eventId }, { rejectWithValue }) => {
        try {
            const config = passXheader
                ? { headers: { "Skip-Event-Active": true, event_id: eventId } }
                : { headers: { event_id: eventId } };
            const backendResponse = await axios.post(`/api/v2/booking-tickets`, attendeesData, config);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            // console.log("Response -> ", response)

            return response
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);


/**
 * @async
 * @function updateBookingById
 * @description Updates a ticket booking by sending a PUT request to the backend API.
 * @param {Object} bookingData - The data containing updated booking information.
 * @param {boolean} passXheader - Flag to determine if the "Skip-Event-Active" header should be included.
 * @param {number} eventId - The ID of the event for which the booking is being updated.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const updateBookingById = createAsyncThunk(
    "ticketBuying/updateBookingById",
    async ({ bookingData, passXheader, eventId }, { rejectWithValue }) => {
        try {
            const config = passXheader
                ? { headers: { "Skip-Event-Active": true, event_id: eventId } }
                : { headers: { event_id: eventId } };
            const backendResponse = await axios.put(`/api/v2/bookings/${bookingData.id}`, bookingData, config);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            // console.log("Response -> ", response)
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);


/**
 * @async
 * @function getTicketBookingById
 * @description Retrieves a ticket booking by its ID by sending a GET request to the backend API.
 * @param {number} bookingId - The ID of the booking to be retrieved.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const getTicketBookingById = createAsyncThunk(
    "ticketBuying/getTicketBookingById",
    async (bookingId, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.get(`/api/v2/bookings/${bookingId}`);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            // console.log("Response -> ", response)
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);


/**
 * @async
 * @function getBookingPaymentStatus
 * @description Retrieves the payment status of a booking by sending a POST request to the backend API.
 * @param {number} bookingId - The ID of the booking for which the payment status is to be retrieved.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const getBookingPaymentStatus = createAsyncThunk(
    "ticketBuying/getBookingPaymentStatus",
    async (bookingId, { rejectWithValue }) => {
        try {
            const backendResponse = await axios.post(`/api/v2/pay`, { booking_id: bookingId });

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }
            // console.log("Response -> ", response)
            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
);


/**
 * TODO: Tell backend to make change in api to add passXheader and eventId
 * @async
 * @function initiatePayment
 * @description Initiates the payment process for a booking by sending a POST request to the backend API.
 * @param {Object} payload - The data containing payment initiation information.
 * @param {boolean} passXheader - Flag to determine if the "Skip-Event-Active" header should be included.
 * @param {number} eventId - The ID of the event for which the payment is being initiated.
 * @returns {Promise<Object>} - The response data from the backend API.
 * @throws Will throw an error if the API request fails.
 */
export const initiatePayment = createAsyncThunk(
    'event/initiatePayment',
    async ({ payload, passXheader, eventId }, { rejectWithValue }) => {
        try {
            const config = passXheader
                ? { headers: { "Skip-Event-Active": true, event_id: eventId } }
                : { headers: { event_id: eventId } };

            const backendResponse = await axios.post(`/api/v2/pay`, payload, config);

            const response = {
                ...backendResponse?.data,
                code: backendResponse?.status
            }

            return response;
        } catch (error) {
            return rejectWithValue({
                ...error?.data,
                code: error?.status
            });
        }
    }
)